"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";

interface Client {
  id: string;
  name: string;
  logoUrl: string;
}

function LogoCard({ client }: { client: Client }) {
  return (
    <div className="flex-shrink-0 w-[180px] h-[90px] bg-white rounded-2xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] flex items-center justify-center px-6 hover:shadow-lg hover:border-slate-200 hover:scale-105 transition-all duration-500 group">
      <img
        src={client.logoUrl}
        alt={client.name}
        loading="lazy"
        className="w-auto max-h-10 max-w-[85%] object-contain grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
      />
    </div>
  );
}

function MarqueeRow({
  items,
  direction = "left",
  repeat = 4,
}: {
  items: Client[];
  direction?: "left" | "right";
  repeat?: number;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let animationId: number;
    let position = direction === "left" ? 0 : -(el.scrollWidth / 2);

    const animate = () => {
      if (direction === "left") {
        position -= 0.5;
        if (Math.abs(position) >= el.scrollWidth / 2) {
          position = 0;
        }
      } else {
        position += 0.5;
        if (position >= 0) {
          position = -(el.scrollWidth / 2);
        }
      }
      el.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [direction]);

  const duplicated = Array.from({ length: repeat }, () => items).flat();

  return (
    <div className="overflow-hidden">
      <div
        ref={scrollRef}
        className="flex items-center gap-4 will-change-transform"
      >
        {duplicated.map((client, idx) => (
          <LogoCard key={`${direction}-${idx}`} client={client} />
        ))}
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { locale } = useLocale();

  useEffect(() => {
    fetch("/api/admin/clients")
      .then((res) => res.json())
      .then((data) => setClients(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  if (clients.length === 0) return null;

  const repeatCount = 1;

  return (
    <section
      ref={sectionRef}
      className="py-14 bg-gradient-to-b from-white via-slate-50/50 to-white overflow-hidden relative"
      id="clients"
    >
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #94a3b8 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      />
      <div className="container mx-auto px-6 relative z-10">
        <div
          className={`text-center mb-10 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-brand-500" />
            <span className="text-brand-600 font-semibold uppercase tracking-[0.25em] text-[11px]">
              {t(locale, "home.clients.badge")}
            </span>
            <div className="w-8 h-px bg-brand-500" />
          </div>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-brand-900 mb-3">
            {t(locale, "home.clients.heading")} <span className="text-brand-600">{t(locale, "home.clients.highlight")}</span>
          </h2>
          <p className="text-slate-500 text-base max-w-lg mx-auto font-light">
            {t(locale, "home.clients.subtext")}
          </p>
        </div>
      </div>

      <MarqueeRow items={clients} direction="left" repeat={repeatCount} />

      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none z-10" />
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none z-10" />
    </section>
  );
}
